import React, {
  ChangeEvent,
  forwardRef,
  KeyboardEvent,
  TextareaHTMLAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import { KeyboardKey } from '../../enums/keyboardKey'
import { InputPropsRaw } from '../Input'
import { ErrorIcon } from '../Input/elements/ErrorIcon'
import { SuccessIcon } from '../Input/elements/SuccessIcon'
import * as Styled from './styles'

type TextareaType = TextareaHTMLAttributes<HTMLTextAreaElement>
type Value = TextareaType['value']
type InputPropsToOmit =
  | 'prefix'
  | 'suffix'
  | 'type'
  | 'onPressEnter'
  | 'defaultValue'
  | 'truncate'
  | 'selectLook'
  | 'isRounded'

export interface BaseTextareaProps extends Omit<TextareaType & InputPropsRaw, InputPropsToOmit> {
  defaultValue?: Value
  onPressEnter?: (event: KeyboardEvent<HTMLTextAreaElement>) => void
  resizable?: boolean
}

export type TextareaProps =
  | (BaseTextareaProps & { expandOnType: true; maxHeight: number }) // Required `maxHeight` when `expandOnType` is true
  | (BaseTextareaProps & { expandOnType?: false; maxHeight?: number })

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      alignment = 'left',
      autoCompleted = false,
      autoFocus = false,
      bordered = true,
      className,
      defaultValue,
      disabled = false,
      error = false,
      expandOnType = false,
      focused = false,
      hidden = false,
      id,
      maxHeight,
      onChange,
      onPressEnter,
      resizable = true,
      rows = 2,
      size = 'md',
      success = false,
      value = '',
      weight = 'regular',
      ...textareaRest
    },
    forwardedRef,
  ) => {
    const [currentValue, setCurrentValue] = useState<Value>(defaultValue ?? value)
    const textareaRef = useRef<HTMLTextAreaElement>(null)

    const adjustHeight = useCallback(() => {
      if (expandOnType && !maxHeight) {
        throw new Error('maxHeight is required when expandOnType is true')
      }

      requestAnimationFrame(() => {
        if (expandOnType && textareaRef.current) {
          textareaRef.current.style.height = 'max-content'
          const scrollHeight = bordered ? textareaRef.current.scrollHeight + 2 : textareaRef.current.scrollHeight
          // Warning disabled during the eslint warning cleanup.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const newHeight = Math.min(scrollHeight, maxHeight!)
          textareaRef.current.style.height = newHeight + 'px'
        }
      })
    }, [bordered, expandOnType, maxHeight])

    useLayoutEffect(adjustHeight, [adjustHeight])

    // Functions

    const handleValueChange = useCallback(
      (newValue: Value) => {
        setCurrentValue(newValue)
        expandOnType && adjustHeight()
      },
      [adjustHeight, expandOnType],
    )

    const handleTextareaChange = useCallback(
      (event: ChangeEvent<HTMLTextAreaElement>) => {
        if (disabled) {
          return
        }

        handleValueChange(event?.currentTarget.value)
        onChange && onChange(event)
      },
      [handleValueChange, onChange, disabled],
    )

    const handleKeyPress = useCallback(
      (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === KeyboardKey.Enter) {
          onPressEnter && onPressEnter(event)
        }
      },
      [onPressEnter],
    )

    // Mutations

    useEffect(() => {
      if (autoFocus) {
        textareaRef.current?.focus()
      }
    }, [autoFocus])

    useEffect(() => {
      handleValueChange(value)
    }, [value, handleValueChange])

    // Hooks utils

    useImperativeHandle(forwardedRef, () => textareaRef?.current as HTMLTextAreaElement)

    return (
      <Styled.TextareaWrapper className={className}>
        <Styled.Textarea
          {...textareaRest}
          alignment={alignment}
          autoCompleted={autoCompleted}
          bordered={bordered}
          className={className}
          disabled={disabled}
          error={error}
          focused={focused}
          hidden={hidden}
          id={id}
          onChange={handleTextareaChange}
          onKeyPress={handleKeyPress}
          ref={textareaRef}
          resizable={resizable}
          rows={rows}
          selectLook={false}
          size={size}
          truncate={false}
          value={currentValue}
          weight={weight}
          withSuffix={error || success}
          isRounded={false}
        />

        {(error || success) && (
          <Styled.SuffixIconWrapper inputSize={size}>
            {error && <ErrorIcon inputSize={size} disabled={disabled} />}
            {success && <SuccessIcon inputSize={size} disabled={disabled} />}
          </Styled.SuffixIconWrapper>
        )}
      </Styled.TextareaWrapper>
    )
  },
)
